import React from "react";
import { motion } from "framer-motion";
import { ShoppingCart, ChevronRight, Star } from "lucide-react";

const IMGS = {
  chips: "/redchips.png",
  chatpata: "/Chatpata-Bnbn_zpU.png",
  aloo: "/Aloo-oUrpi2pO.png",
  salted: "/Salted-lkWK5iNw.png",
};

const scrollTo = (id) =>
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const FloatImg = ({ src, alt, className, delay = 0, rotate = 0 }) => (
  <motion.div
    className={className}
    animate={{ y: [0, -18, 0], rotate: [rotate, rotate + 2, rotate] }}
    transition={{ duration: 4 + delay, repeat: Infinity, ease: "easeInOut", delay }}
  >
    <img
      src={src}
      alt={alt}
      className="w-full drop-shadow-2xl mix-blend-multiply"
    />
  </motion.div>
);

const Hero = () => {
  return (
    <section
      id="home"
      className="relative min-h-screen pt-24 pb-16 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background dot pattern */}
      <div className="absolute inset-0 snack-pattern pointer-events-none" />

      {/* Decorative blobs */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-orange-200/30 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-yellow-200/40 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-[85vh] flex items-center">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center w-full">
          {/* Left: Text */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="space-y-7"
          >


            {/* Headline */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-stone-900 leading-[1.05]">
              India's{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500">
                Trusted
              </span>
              <br />
              Snacking
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-orange-500">
                Partner
              </span>
            </h1>

            {/* Sub-text */}
            <p className="text-lg sm:text-xl text-stone-600 leading-relaxed max-w-lg">
              From crispy chips to flavourful namkeen — MunchhoNN brings
              authentic Indian snacks crafted with love, quality, and tradition.{" "}
              <span className="font-bold text-orange-600">Mood Banaye Mast!</span>
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => scrollTo("products")}
                className="flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full px-8 py-4 font-bold text-lg shadow-xl hover:shadow-orange-500/40 hover:scale-105 transition-all duration-200 btn-glow"
                data-testid="hero-shop-now-btn"
              >
                <ShoppingCart size={20} />
                Shop Now
              </button>
              <button
                onClick={() => scrollTo("products")}
                className="flex items-center justify-center gap-2 bg-yellow-400 text-stone-900 rounded-full px-8 py-4 font-bold text-lg shadow-md hover:bg-yellow-500 hover:scale-105 transition-all duration-200"
                data-testid="hero-view-products-btn"
              >
                View Products
                <ChevronRight size={20} />
              </button>
            </div>

            {/* Stats */}
            <div className="flex gap-8 pt-2">
              {[
                { value: "40+", label: "Years Experience" },
                { value: "1M+", label: "Happy Customers" },
                { value: "50+", label: "Products" },
              ].map((s) => (
                <div key={s.label}>
                  <p className="text-3xl font-black text-orange-500">{s.value}</p>
                  <p className="text-xs text-stone-500 font-medium">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Rating row */}
            <div className="flex items-center gap-2">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} className="fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <span className="text-sm text-stone-500 font-medium">
                4.9/5 from 12,000+ reviews
              </span>
            </div>
          </motion.div>

          {/* Right: Floating product images */}
          <div className="relative h-[500px] hidden lg:block" data-testid="hero-products-display">
            {/* Main chips bag - center */}
            <FloatImg
              src={IMGS.chips}
              alt="Potato Chips Tomato Twist"
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 z-30"
              delay={0}
              rotate={0}
            />
            {/* Chatpata Mix - top left */}
            <FloatImg
              src={IMGS.chatpata}
              alt="Chatpata Mix"
              className="absolute top-8 left-0 w-52 z-20"
              delay={1}
              rotate={-14}
            />
            {/* Aloo Bhujia - top right */}
            <FloatImg
              src={IMGS.aloo}
              alt="Aloo Bhujia"
              className="absolute top-12 right-0 w-52 z-10"
              delay={0.5}
              rotate={12}
            />
            {/* Salted Pipe - bottom left */}
            <FloatImg
              src={IMGS.salted}
              alt="Salted Pipe"
              className="absolute bottom-8 left-12 w-44 z-20"
              delay={1.5}
              rotate={8}
            />
          </div>

          {/* Mobile single product */}
          <motion.div
            className="lg:hidden flex justify-center"
            animate={{ y: [0, -12, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          >
            <img
              src={IMGS.chips}
              alt="MunchhoNN Products"
              className="w-64 drop-shadow-2xl mix-blend-multiply"
            />
          </motion.div>
        </div>
      </div>

      {/* Wave bottom divider */}
      <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path
            d="M0 60L80 50C160 40 320 20 480 15C640 10 800 20 960 27C1120 34 1280 33 1360 28L1440 22V60H1360C1280 60 1120 60 960 60C800 60 640 60 480 60C320 60 160 60 80 60H0Z"
            fill="white"
          />
        </svg>
      </div>
    </section>
  );
};

export default Hero;
